import { IDictionary } from 'util/domain';
import { CommonUtilsService } from 'util/services/commonUtilsService';
import { ReceivedLinkShare } from 'sharebase/domain';
import SettingsService from 'phoenix/services/settingsService';
import {
	DefaultRight,
	RightsOption,
	AclPrincipal,
	ObjectType,
	Acl,
	ShareEntity,
	RightsType,
	UserEntity,
	UserGroupEntity
} from 'phoenix/domain'

/**
* @ngdoc service
* @name phoenix.acl.pxAclService
* @description
* A service used to create and interrogate ACL instances.
**/
export class AclService {
	public static $inject = [
		'utils',
		'settingsService',
		'gettextCatalog'
	];	

	private readonly objectTypeMappings:string[] = this.generateObjectTypeMappings();
	private readonly rights:IDictionary<DefaultRight> = this.generateRights();
	private readonly rightsOptions = [
		null, 
		null, 
		this.generateEditRightsOptions(),
		this.generateViewRightsOptions(), 
		this.generateUpoadOnlyRightsOptions(), 
		null
	];

	constructor(
		private util:CommonUtilsService,
		private settingsService:SettingsService,
		private gettextCatalog:ng.gettext.gettextCatalog
	) {
	}

	/**
	* @ngdoc method
	* @name pxAclService#create
	* @methodOf phoenix.acl.pxAclService
	* @kind function
	*
	* @description
	* Creates a new ACL instance using the most permissive arguments.
	*
	* @param {int} id The ID of the entity to generate an ACL for.
	*
	* @param {string} objectType The type of resource to generate an ACL for.
	*
	* @param {string} name The name of the entity to generate an ACL for.
	*
	* @param {bool} isGroup Value indiciating if this ACL is intended for a group.
	*
	* @returns {Object} A new Acl instance.
	* 
	*/
	public create(principal:AclPrincipal, objectType:ObjectType = ObjectType.UNDEFINED, name:string = '', isGroup:boolean = false, id?:number) {
		return new Acl(
			this.util, 
			this.rights, 
			this.rightsOptions, 
			this.objectTypeMappings, 
			this.settingsService, 
			principal, 
			objectType, 
			name, 
			isGroup, 
			id
		);
	}
	
	public generateEffectiveRights(...acls:Acl[]) {
		let effectiveAcl = this.create(0, ObjectType.UNDEFINED, '', false);
		if(acls.length > 0){
			let compositePrivs = acls.reduce((allPrivs, curr) => allPrivs | curr.allowPerms, 0);
			let compositeDenyPrivs = acls.reduce((allPrivs, curr) => allPrivs | curr.denyPerms, 0);
			// flip deny privs to combine with allow privs				
			let effectivePrivs = compositePrivs & (~compositeDenyPrivs);
			
			// get ACL information for the current user and set them on the effective ACL
			let userAcl = acls.filter(acl => angular.isDefined(acl.userName))[0];
			if(userAcl){
				effectiveAcl = this.create(effectivePrivs, userAcl.type, userAcl.userName, false);
			}
			// use the default
			else {
				effectiveAcl.allowPerms = effectivePrivs;
			}
		}

		return effectiveAcl;
	}

	/**
	* @ngdoc method
	* @name pxAclService#generateForEntity
	* @methodOf phoenix.acl.pxAclService
	* @kind function
	*
	* @description
	* Convenience method that creates a new ACL instance for users or user groups.
	*
	* @param {object} entity A user or group to generate an ACL for.
	*
	* @param {string} objectType The type of resource to generate an ACL for.
	*
	* @returns {Acl} A new Acl instance.
	* 
	*/
	public generateForEntity(entity:ShareEntity, objectType:ObjectType) {
		let id;
		let name;
		let isGroup = false;
		if(isUserEntity(entity)) {
			id = entity.UserId;
			name = entity.UserName;
		} else if (isUserGroupEntity(entity)) {
			id = entity.UserGroupId;
			name = entity.UserGroupName;
			isGroup = true;
		}

		return this.create(null, objectType, name, isGroup, id);
	}

	/**
	* @ngdoc method
	* @name pxAclService#isAcl
	* @methodOf phoenix.acl.pxAclService
	* @kind function
	*
	* @description
	* Verifies if the given object is an instance of an ACL.
	*
	* @param {object} obj The object to test.
	*
	* @returns {bool} True if the object is an instance of Acl.
	* 
	*/
	public static isAcl(obj): obj is Acl {
		return !!obj && (obj instanceof Acl);
	}

	/**
	* @ngdoc method
	* @name pxAclService#toRoleNameFromId
	* @methodOf phoenix.acl.pxAclService
	* @kind function
	*
	* @description
	* Converts a Phoenix role ID to its human-readable name.
	*
	* @param {string} roleId An string with a combination of an integer ID representing
	* an ownership role, possibly with an asterisk or other character appended.
	*
	* @returns {string} Human readable role name localize for the current language.
	* 
	*/
	public toRoleNameFromId(roleId:RightsType) {
		let result = '';

		if(roleId in this.rights){
			result = `${roleId < 5 ? ', ' : ''}${this.rights[roleId].name}`;
			
		}

		return result;
	}

	/**
	* @ngdoc method
	* @name pxAclService#toTypeNameFromId
	* @methodOf phoenix.acl.pxAclService
	* @kind function
	*
	* @description
	* Converts a phoenix resource type ID to its human-readable name.
	*
	* @param {int} typeId An integer ID representing a Phoenix resource type.
	*
	* @returns {string} Human readable role name localize for the current language.
	* 
	*/
	public toTypeNameFromId(typeId:ObjectType) {
		return this.objectTypeMappings[typeId] || '';
	}

	private generateEditRightsOptions() {
		const self = this;
		return [{
			id: 0,
			get name() { return self.gettextCatalog.getString('Share with Users'); },
			mask: [0x01000, 0x211000, 0x211000],
			apply: 'bits',
			constr: ''
		}, {
			id: 1,
			get name() { return self.gettextCatalog.getString('Share View Link'); },
			mask: [0x02000, 0x422000, 0x422000],
			apply: 'bits',
			constr: ''
		}, {
			id: 2,
			get name() { return self.gettextCatalog.getString('Share Edit Link'); },
			mask: [0x04000, 0x844000, 0x844000],
			apply: 'bits',
			constr: '+1'
		}, {
			id: 3,
			get name() { return self.gettextCatalog.getString('Delete'); },
			mask: [0x00004, 0x00444, 0x00444],
			apply: 'bits',
			constr: ''
		}];
	}
	
	private generateViewRightsOptions() {
		const self = this;
		return [{
			id: 0,
			get name() { return self.gettextCatalog.getString('Upload'); },
			mask: [0x00, 0x00088, 0x00088],
			apply: 'bits',
			constr: ''
		}, {
			id: 2,
			get name() { return self.gettextCatalog.getString('Download'); },
			mask: [0x08000, 0x188000, 0x188000],
			apply: 'bits',
			constr: ''
		}, {
			id: 3,
			get name() { return self.gettextCatalog.getString('Share View Link'); },
			mask: [0x02000, 0x422000, 0x422000],
			apply: 'bits',
			constr: ''
		}];
	}
	
	private generateUpoadOnlyRightsOptions() {
		const self = this;
		return [{
			id: 0,
			get name() { return self.gettextCatalog.getString('View Folder Contents'); },
			mask: [0x00, 0x800, 0],
			apply: 'bits',
			constr: ''
		}];
	}

	private generateObjectTypeMappings() {
		const self = this;
		let mappings:string[] = [];
		mappings[ObjectType.DOCUMENT] = self.gettextCatalog.getString('Document');
		mappings[ObjectType.FOLDER] = self.gettextCatalog.getString('Folder');
		mappings[ObjectType.LIBRARY] = self.gettextCatalog.getString('Library');
		return mappings;
	}

	private generateRights() {
		const self = this;
		let Rights:IDictionary<DefaultRight> = {};
		// For some reason, the calls to gettextCatalog.getString are getting executed
		// before the language service is bootstrapped with the app language. To mitigate
		// this, the name properties are getters so the calls are executed later.
		// Hopefully, this should be fixed if/when SBWEB-1020 gets implemented and the
		// if we include language bootstrapping in there.
		Rights[RightsType.UNDEFINED] = {
			id:  RightsType.UNDEFINED, 
			defaultPerms: {
				document: 0,
				folder: 0,
				library: 0
			}, 
			opts: false, 
			name: '' 
		};
	
		Rights[RightsType.FULLCONTROL] = {
			id:  RightsType.FULLCONTROL,
			defaultPerms: {
				document: -1,
				folder: -1,
				library: -1
			},
			opts: false,
			get name() { return self.gettextCatalog.getString('Full Control') }
		};
		
		Rights[RightsType.EDIT] =  { 
			id:  RightsType.EDIT,
			defaultPerms: {
				document: 	0b110001000000000000011, 
				folder: 	0b110001000101110111011, 
				library: 	0b110001000101110111011
			},
			opts: true,
			get name() { return  self.gettextCatalog.getString('Edit'); }
		};
		
		Rights[ RightsType.VIEW] ={ 
			id:  RightsType.VIEW, 
			defaultPerms: {
				document: 	0b0000000000000001, 
				folder: 	0b000000000100100010001, 
				library:	0b000000000100100010001
			},
			opts: true,
			get name() { return  self.gettextCatalog.getString('View'); }
		};
		
		Rights[RightsType.UPLOADONLY] = {
			id: RightsType.UPLOADONLY,
			defaultPerms: {
				document: 0, 
				folder: 0b1001, 
				library: 0
			},
			opts: true,
			get name() { return self.gettextCatalog.getString('Upload Only'); }
		};
		
		Rights[RightsType.NOACCESS] =  {
			id:  RightsType.NOACCESS,
			defaultPerms: {
				document: 0,
				folder: 0,
				library: 0
			},
			opts: false,
			get name() { return self.gettextCatalog.getString('No Access'); }
		};
	
		return Rights;
	}
}

function isUserEntity(e): e is UserEntity {
	return angular.isDefined(e.UserName) && angular.isNumber(e.UserId);
}

function isUserGroupEntity(e): e is UserGroupEntity {
	return angular.isDefined(e.UserGroupName) && angular.isNumber(e.UserGroupId);
}


// WEBPACK FOOTER //
// ./src/phoenix/services/aclService.ts